import { FC, useMemo } from 'react'
import { useRecoilValue } from 'recoil'
import styled from '@emotion/styled'
import { Colors, Shapes } from 'src/components/theme'
import { UnavailableCardContent } from 'src/components/insights/unavailable-card-content'
import { ZoneDetailResponse } from 'src/common-services/types/zone-types'
import { Typography } from '@mui/material'
import { capitalCase } from 'capital-case'
import { FlexColumn } from 'src/components/flex-containers'

import * as atoms from 'src/atoms/zone'

const Wrapper = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  padding-inline: ${Shapes.spacing[4]}px;
  gap: ${Shapes.spacing[4]}px;
  &:empty {
    display: none;
  }
`
const List = styled.ul`
  margin-block: 0;
`

const StandardsList: React.FC<{ controls: ZoneDetailResponse['controls'] }> = ({ controls }) => {
  // in 3 columns
  const cleanedControls = useMemo(() => {
    if (!controls) {
      return []
    }
    // convert the record into an array of objects with the key and value
    const array = Object.entries(controls)
      .map(([key, value]) => ({
        controlKey: key,
        controlValue: value,
      }))
      .filter((item) => item.controlValue !== 'NA' && item.controlValue !== 'None')
    return array
  }, [controls])

  if (cleanedControls.length === 0) {
    return <UnavailableCardContent message="No Zoning Codes Available" />
  }

  const renderValue = (value: string) => {
    const formattedData = []

    let formattedValue

    // Split on ", " only if followed by a capital letter (avoiding splits within numbers/parentheses)
    if (/, [A-Z]/.test(value)) {
      formattedValue = value.split(/, (?=[A-Z])/).map((item) => item.trim())
    } else {
      formattedValue = [value.trim()]
    }

    // Format numbers over 1000 with commas
    const formattedWithCommas = formattedValue.map((item) =>
      item.replace(/\b\d{4,}\b/g, (num) => Number(num).toLocaleString())
    )

    formattedData.push(...formattedWithCommas)

    return formattedData.map((item, index) => (
      <li key={index}>
        <Typography variant="text1m" color={Colors.neutral500}>
          {item}
        </Typography>
      </li>
    ))
  }

  return (
    <Wrapper>
      {cleanedControls.map((item) => (
        <FlexColumn key={item.controlKey} gap={Shapes.spacing[1]}>
          <Typography variant="text1" color={Colors.neutral700}>
            {capitalCase(item.controlKey)}
          </Typography>
          <List>{renderValue(item.controlValue)}</List>
        </FlexColumn>
      ))}
    </Wrapper>
  )
}

type ZoningCodesListContentProps = {
  details?: ZoneDetailResponse
}

export const ZoningCodesListContent: FC<ZoningCodesListContentProps> = ({ details }) => {
  if (!details || !details.controls) {
    return <UnavailableCardContent message="No Zoning Codes Available" />
  }

  return <StandardsList controls={details.controls} />
}

export const ZoningCodesList = () => {
  const details = useRecoilValue(atoms.zoningDetailsForGooglePlace)
  if (!details) {
    return <UnavailableCardContent message="No Zoning Codes Available" />
  }
  return <ZoningCodesListContent details={details} />
}
